import React, { ReactNode } from "react";
import {
  ExtractParameter,
  OAuth2Type,
  useOAuth2,
} from "../OAuth2Context";
import { OAuth2QueryParameters } from "@features/authentication/constants/Oauth2";
import ErrorPage from "@pages/ErrorPage";
import { isSuccessTokenStepResponse } from "@shared/utils/typeGuards";
import { LoadingPage } from "@components/ui";
import { deleteState, verifyState } from "@shared/utils/storage/stateStorage";
import { ApiError } from "@shared/services";
import { useLocation } from "react-router-dom";
import { JwtDecoder } from "@shared/utils/jwt/JwtDecoder";
import { PagePaths } from "@shared/constants";
import { ComposeURLWithGetQueryArguments } from "@shared/utils";
import { useTranslation } from "react-i18next";

type RenderOAuth2Props = { children: ReactNode };

const REDIRECT_URI_KEY = "oauth2_redirect_uri";

/** Persist the client redirect_uri so we can recover after a page refresh. */
function persistRedirectUri(uri: string): void {
  if (!uri) return;
  try { sessionStorage.setItem(REDIRECT_URI_KEY, uri); }
  catch { /* quota exceeded or unavailable -- ignore */ }
}

/** Retrieve the persisted redirect_uri (survives F5 refresh). */
export function getPersistedRedirectUri(): string | null {
  try { return sessionStorage.getItem(REDIRECT_URI_KEY); }
  catch { return null; }
}

// Helper function to check if current page is recovery password page
const isRecoveryPasswordPage = (currentPage: string): boolean => {
  return currentPage === PagePaths.LOGIN.RECOVERYPASSWORD || 
         currentPage.startsWith(PagePaths.LOGIN.RECOVERYPASSWORD);
};

const handleInvalidRoute = (isInvalidRoute: boolean): boolean => {
  return isInvalidRoute;
};

const handleRootRouteWithoutOAuth2 = (currentPage: string, oauth2Data: OAuth2Type): boolean => {
  return currentPage === "/" && !hasRequiredOAuth2Parameters(oauth2Data);
};

const shouldRedirectToUri = (currentPage: string, redirectUri: string): boolean => {
  return currentPage !== "/" && !!redirectUri && redirectUri !== currentPage && !currentPage.includes(redirectUri);
};

const handleInvalidState = (
  currentPage: string,
  oauth2Data: OAuth2Type
): React.ReactElement | null => {
  if (isRecoveryPasswordPage(currentPage)) {
    return null;
  }
  
  const redirectTarget = oauth2Data.redirect_uri || getPersistedRedirectUri();
  if (redirectTarget && shouldRedirectToUri(currentPage, redirectTarget)) {
    window.location.href = redirectTarget;
    return <LoadingPage />;
  }
  
  const missingArguments = getMissingOauth2Arguments(oauth2Data);
  return <ErrorPage errorType="oauth" missingArguments={missingArguments} />;
};

const handleMissingOAuth2Parameters = (oauth2Data: OAuth2Type): React.ReactElement => {
  const persistedUri = getPersistedRedirectUri();
  if (persistedUri) {
    window.location.href = persistedUri;
    return <LoadingPage />;
  }
  const missingArguments = getMissingOauth2Arguments(oauth2Data);
  return <ErrorPage errorType="oauth" missingArguments={missingArguments} />;
};

const RECAPTCHA_CHECKS: Array<(error: ApiError, recaptchaError: string | null) => boolean> = [
  (_e, r) => !!r,
  (e) => e.statusText === "reCAPTCHA Error",
  (e) => !!e.message?.includes("reCAPTCHA"),
  (e) => (e.body as any)?.errorCode === "RECAPTCHA_ERROR",
];

const isRecaptchaError = (error: ApiError, recaptchaError: string | null): boolean => {
  return RECAPTCHA_CHECKS.some(check => check(error, recaptchaError));
};

const TIMEOUT_CHECKS: Array<(error: ApiError, isTimeout: boolean) => boolean> = [
  (_e, t) => t,
  (e) => !!e.message?.includes("timeout"),
  (e) => e.statusText === "Timeout",
];

const isTimeoutErrorCheck = (error: ApiError, isTimeout: boolean): boolean => {
  return TIMEOUT_CHECKS.some(check => check(error, isTimeout));
};

const handleTimeoutError = (): React.ReactElement => {
  return (
    <ErrorPage 
      errorType="timeout" 
      missingArguments={[]}
    />
  );
};

const handleRecaptchaError = (error: ApiError, recaptchaError: string | null): React.ReactElement => {
  return (
    <ErrorPage 
      errorType="oauth" 
      errorCode="RECAPTCHA_ERROR"
      errorMessage={recaptchaError || error.message}
      missingArguments={[]}
    />
  );
};

const firstTruthy = (...values: (string | undefined | null)[]): string =>
  values.find(v => !!v) ?? "UNKNOWN_ERROR";

const extractErrorCode = (body: any, error: ApiError): string =>
  firstTruthy(body?.error_code, body?.errorCode, error.statusText, error.status ? `HTTP_${error.status}` : undefined);

const extractErrorMessage = (body: any, error: ApiError, t: (key: string) => string): string =>
  body?.detail || error.message || error.statusText || t("error.oauth.unexpected");

const extractErrorDetails = (error: ApiError, t: (key: string) => string): { errorCode: string; errorMessage: string; errorTitle?: string } => {
  const body = error.body as any;
  return {
    errorCode: extractErrorCode(body, error),
    errorMessage: extractErrorMessage(body, error, t),
    errorTitle: body?.title,
  };
};

const handleGenericOAuthError = (error: ApiError, t: (key: string) => string): React.ReactElement => {
  const { errorCode, errorMessage, errorTitle } = extractErrorDetails(error, t);
  return (
    <ErrorPage 
      errorType="oauth" 
      errorCode={errorCode}
      errorMessage={errorMessage}
      errorTitle={errorTitle}
      missingArguments={[]}
    />
  );
};

const handleTimeoutResponseCheck = (
  startAuthenticationRequestResponse: any,
  isTimeout: boolean,
  recaptchaError: string | null,
  t: (key: string) => string
): React.ReactElement | null => {
  if (!isTimeout) return null;

  const hasFailedResponse = startAuthenticationRequestResponse && 
    !isSuccessTokenStepResponse(startAuthenticationRequestResponse);
  const isConfirmedTimeout = !hasFailedResponse || 
    isTimeoutErrorCheck(startAuthenticationRequestResponse as ApiError, isTimeout);

  return isConfirmedTimeout ? handleTimeoutError() : null;
};

const handleFailedAuthResponse = (
  startAuthenticationRequestResponse: any,
  isTimeout: boolean,
  recaptchaError: string | null,
  t: (key: string) => string
): React.ReactElement | null => {
  if (isSuccessTokenStepResponse(startAuthenticationRequestResponse)) {
    return null;
  }
  const error = startAuthenticationRequestResponse as ApiError;
  
  if (isRecaptchaError(error, recaptchaError)) {
    return handleRecaptchaError(error, recaptchaError);
  }
  if (isTimeoutErrorCheck(error, isTimeout)) {
    return handleTimeoutError();
  }
  return handleGenericOAuthError(error, t);
};

const handleAuthenticationResponse = (
  startAuthenticationRequestResponse: any,
  isTimeout: boolean,
  recaptchaError: string | null,
  t: (key: string) => string
): React.ReactElement | null => {
  const timeoutResult = handleTimeoutResponseCheck(startAuthenticationRequestResponse, isTimeout, recaptchaError, t);
  if (timeoutResult) return timeoutResult;

  if (!startAuthenticationRequestResponse) {
    return <LoadingPage />;
  }

  return handleFailedAuthResponse(startAuthenticationRequestResponse, isTimeout, recaptchaError, t);
};

const isOAuthAuthorizedCallbackPath = (path: string): boolean => {
  return path.includes("/oauth2/oidc/") && path.includes("/authorized");
};

const isProviderEmailNotVerifiedError = (errorCode: string | null): boolean =>
  errorCode === "PROVIDER_EMAIL_NOT_VERIFIED";

const handleProviderEmailNotVerifiedError = (
  location: any,
  state: string
): React.ReactElement | null => {
  const errorCode = ExtractParameter("error_code", location);
  if (!isProviderEmailNotVerifiedError(errorCode)) return null;

  deleteState(state);
  return (
    <ErrorPage
      errorType="social"
      errorCode={errorCode}
      missingArguments={[]}
    />
  );
};

const handleEmailNotVerifiedRedirect = (
  location: any,
  state: string
): React.ReactElement | null => {
  const errorCode = ExtractParameter("error_code", location);
  if (errorCode !== "EMAIL_NOT_VERIFIED") return null;

  const verificationToken = ExtractParameter("verification_token", location);
  const email = ExtractParameter("email", location);

  if (!verificationToken || !email) {
    deleteState(state);
    return <ErrorPage errorType="social" errorCode={errorCode} missingArguments={[]} />;
  }

  sessionStorage.setItem('isSocialLogin', 'true');
  sessionStorage.setItem('socialLoginToken', JSON.stringify({
    access_token: verificationToken,
    token_type: 'Bearer',
  }));
  sessionStorage.setItem('emailNotVerifiedEmail', email);

  const verifyEmailUrl = ComposeURLWithGetQueryArguments(PagePaths.LOGIN.VERIFYEMAIL);
  window.location.href = verifyEmailUrl;
  return <LoadingPage />;
};

const handleOAuthAuthorizedCallbackError = (
  currentPage: string,
  location: any,
  state: string
): React.ReactElement | null => {
  if (!isOAuthAuthorizedCallbackPath(currentPage)) {
    return null;
  }

  const errorCode = ExtractParameter("error_code", location);
  const errorMessage = ExtractParameter("error_message", location);

  if (errorCode && errorMessage) {
    deleteState(state);
    return (
      <ErrorPage 
        errorType="oauth" 
        errorCode={errorCode}
        errorMessage={errorMessage}
        missingArguments={[]}
      />
    );
  }

  return null;
};

const SOCIAL_LOGIN_REFERRER_PATHS = ['/oauth2/oidc/google/', '/oauth2/oidc/facebook/'];

const isSocialLoginCallback = (): boolean => {
  const referrer = document.referrer;
  return !!referrer && SOCIAL_LOGIN_REFERRER_PATHS.some(path => referrer.includes(path));
};

const getSocialLoginProvider = (): 'google' | 'facebook' | null => {
  const referrer = document.referrer;
  if (!referrer) return null;
  if (referrer.includes('/oauth2/oidc/google/')) return 'google';
  if (referrer.includes('/oauth2/oidc/facebook/')) return 'facebook';
  return null;
};

const EMAIL_CLAIM_PATHS: Array<(claims: any) => string | undefined> = [
  (c) => c.email,
  (c) => c.additional_claims?.email,
  (c) => c.additional_claims?.user_email,
];

const extractEmailFromClaims = (claims: any): string | null => {
  const found = EMAIL_CLAIM_PATHS.find(path => path(claims));
  return found ? found(claims) || null : null;
};

// Helper function to extract email verified status from claims
const extractEmailVerifiedFromClaims = (claims: any): boolean | undefined => {
  return claims.email_verified ?? claims.additional_claims?.email_verified ?? undefined;
};

const PROVIDER_DETECTORS: Array<{ check: (claims: any) => boolean; provider: string }> = [
  { check: (c) => c.iss?.includes('google') || c.additional_claims?.provider === 'google', provider: 'google' },
  { check: (c) => c.iss?.includes('facebook') || c.additional_claims?.provider === 'facebook', provider: 'facebook' },
];

const detectProviderFromClaims = (claims: any): string | undefined => {
  return PROVIDER_DETECTORS.find(d => d.check(claims))?.provider;
};

const checkUserEmailFromToken = (token: string): { 
  hasEmail: boolean; 
  email?: string; 
  provider?: string;
  emailVerified?: boolean;
} => {
  try {
    const claims = JwtDecoder.getAllClaims(token);
    if (!claims) {
      return { hasEmail: false };
    }

    const email = extractEmailFromClaims(claims);
    const emailVerified = extractEmailVerifiedFromClaims(claims);
    const provider = detectProviderFromClaims(claims);

    return {
      hasEmail: !!email && email.trim() !== '',
      email: email || undefined,
      provider,
      emailVerified
    };
  } catch (error) {
    console.error('[RenderOAuth2] Error checking email from token:', error);
    return { hasEmail: false };
  }
};

const checkEmailValidatedInAuthien = (token: string): boolean => {
  try {
    const claims = JwtDecoder.getAllClaims(token);
    if (!claims) {
      return false;
    }

    return extractEmailVerifiedFromClaims(claims) === true;
  } catch (error) {
    console.error('[RenderOAuth2] Error checking email validation in Authien:', error);
    return false;
  }
};

const isOnboardingCompleted = (nextStep: string): boolean => {
  // If next_step is /external/onboarding, onboarding is not completed
  return nextStep !== PagePaths.ONBOARDING && !nextStep.includes('/external/onboarding');
};

// Helper function to check if current page matches redirect URI
const isCurrentPageMatchingRedirectUri = (currentPage: string, redirectUri: string): boolean => {
  if (!redirectUri) {
    return false;
  }

  try {
    const redirectUriPath = new URL(redirectUri).pathname;
    return currentPage === redirectUriPath;
  } catch {
    return currentPage === redirectUri || redirectUri.includes(currentPage);
  }
};

const SOCIAL_LOGIN_ERRORS: Record<string, { noEmail: { code: string; message: string }; unverified: { code: string; message: string } }> = {
  facebook: {
    noEmail: { code: 'FACEBOOK_NO_EMAIL', message: 'Facebook account must have an email address to login. Please use a Facebook account with an email or use another login method.' },
    unverified: { code: 'FACEBOOK_EMAIL_NOT_VERIFIED', message: 'Your Facebook email address is not verified. Please verify your email address in Facebook and try again.' },
  },
  google: {
    noEmail: { code: 'GOOGLE_NO_EMAIL', message: 'Google account must have an email address to login. Please use a Google account with an email or use another login method.' },
    unverified: { code: 'GOOGLE_EMAIL_NOT_VERIFIED', message: 'Your Google email address is not verified. Please verify your email address in Google and try again.' },
  },
};

const buildSocialLoginErrorPage = (
  errorCode: string,
  errorMessage: string,
  location: any,
  state: string
): React.ReactElement => {
  const stateParam = ExtractParameter(OAuth2QueryParameters.STATE, location);
  deleteState(stateParam);
  return (
    <ErrorPage 
      errorType="oauth" 
      errorCode={errorCode}
      errorMessage={errorMessage}
      missingArguments={[]}
    />
  );
};

const isKnownSocialProvider = (provider: string | null): provider is 'facebook' | 'google' =>
  provider === 'facebook' || provider === 'google';

const isEmailUnverifiedForOnboarding = (emailCheck: { emailVerified?: boolean }, onboardingCompleted: boolean): boolean =>
  emailCheck.emailVerified === false && !onboardingCompleted;

const handleSocialLoginEmailErrors = (
  provider: 'google' | 'facebook' | null,
  emailCheck: { hasEmail: boolean; emailVerified?: boolean },
  onboardingCompleted: boolean,
  location: any
): React.ReactElement | null => {
  if (!isKnownSocialProvider(provider)) return null;

  const errors = SOCIAL_LOGIN_ERRORS[provider];

  if (!emailCheck.hasEmail) {
    return buildSocialLoginErrorPage(errors.noEmail.code, errors.noEmail.message, location, '');
  }

  if (isEmailUnverifiedForOnboarding(emailCheck, onboardingCompleted)) {
    return buildSocialLoginErrorPage(errors.unverified.code, errors.unverified.message, location, '');
  }

  return null;
};

// Helper function to store social login data in sessionStorage
const storeSocialLoginData = (provider: 'google' | 'facebook' | null, tokenResponse: any): void => {
  try {
    sessionStorage.setItem('isSocialLogin', 'true');
    if (provider) {
      sessionStorage.setItem('socialLoginProvider', provider);
    }
    sessionStorage.setItem('socialLoginToken', JSON.stringify(tokenResponse));
  } catch (e) {
    console.warn('Failed to store social login indicator:', e);
  }
};

// Helper function to handle social login redirect scenarios
const handleSocialLoginRedirect = (
  nextStep: string,
  onboardingCompleted: boolean,
  emailValidatedInAuthien: boolean
): React.ReactElement => {
  if (!onboardingCompleted) {
    // Scenario 3: User needs to complete onboarding
    window.location.href = nextStep;
    return <LoadingPage />;
  }

  // Onboarding completed, redirect to email verification
  const verifyEmailUrl = ComposeURLWithGetQueryArguments(PagePaths.LOGIN.VERIFYEMAIL);
  window.location.href = verifyEmailUrl;
  return <LoadingPage />;
};

// Helper function to handle social login callback
const handleSocialLoginCallback = (
  startAuthenticationRequestResponse: any,
  location: any
): React.ReactElement | null => {
  const provider = getSocialLoginProvider();
  const emailCheck = checkUserEmailFromToken(startAuthenticationRequestResponse.access_token);
  const nextStep = startAuthenticationRequestResponse.next_step;
  const onboardingCompleted = isOnboardingCompleted(nextStep);
  const emailValidatedInAuthien = checkEmailValidatedInAuthien(startAuthenticationRequestResponse.access_token);

  // Check for email validation errors
  const emailError = handleSocialLoginEmailErrors(provider, emailCheck, onboardingCompleted, location);
  if (emailError) {
    return emailError;
  }

  // Store social login data
  storeSocialLoginData(provider, startAuthenticationRequestResponse);

  // Handle redirect scenarios
  return handleSocialLoginRedirect(nextStep, onboardingCompleted, emailValidatedInAuthien);
};

const isCallbackMatchForRedirect = (
  currentPage: string,
  redirectUri: string,
  response: any,
): boolean =>
  isCurrentPageMatchingRedirectUri(currentPage, redirectUri) &&
  isSuccessTokenStepResponse(response) &&
  !!response.next_step;

const shouldProcessSocialLogin = (response: any): boolean =>
  isSocialLoginCallback() && !!response.access_token;

const handleCallbackUrlRedirect = (
  currentPage: string,
  redirectUri: string,
  startAuthenticationRequestResponse: any,
  location: any
): React.ReactElement | null => {
  if (!isCallbackMatchForRedirect(currentPage, redirectUri, startAuthenticationRequestResponse)) {
    return null;
  }

  if (shouldProcessSocialLogin(startAuthenticationRequestResponse)) {
    return handleSocialLoginCallback(startAuthenticationRequestResponse, location);
  }

  window.location.href = startAuthenticationRequestResponse.next_step;
  return <LoadingPage />;
};

const handleRouteValidation = (currentPage: string, oauth2Data: OAuth2Type, children: ReactNode): React.ReactElement | null => {
  const isInvalidRoute = !isValidApplicationRoute(currentPage);
  
  if (handleInvalidRoute(isInvalidRoute)) {
    return <>{children}</>;
  }

  if (handleRootRouteWithoutOAuth2(currentPage, oauth2Data)) {
    return <>{children}</>;
  }

  return null;
};

const isEmailVerificationPage = (currentPage: string): boolean => {
  return currentPage === PagePaths.LOGIN.VERIFYEMAIL;
};

const handleStateValidation = (
  state: string,
  currentPage: string,
  oauth2Data: OAuth2Type,
  location: any
): React.ReactElement | null => {
  if (isRecoveryPasswordPage(currentPage) || isEmailVerificationPage(currentPage)) {
    return null;
  }
  
  if (ExtractParameter("error", location)) {
    deleteState(state);
  }

  if (!verifyState(state)) {
    return handleInvalidState(currentPage, oauth2Data);
  }

  return null;
};

const handleAuthenticationFlow = (
  oauth2Data: OAuth2Type,
  startAuthenticationRequestResponse: any,
  isTimeout: boolean,
  recaptchaError: string | null,
  t: (key: string) => string,
  currentPage: string
): React.ReactElement | null => {
  if (isRecoveryPasswordPage(currentPage) || isEmailVerificationPage(currentPage)) {
    return null;
  }
  
  if (!hasRequiredOAuth2Parameters(oauth2Data)) {
    return handleMissingOAuth2Parameters(oauth2Data);
  }

  return handleAuthenticationResponse(startAuthenticationRequestResponse, isTimeout || false, recaptchaError, t);
};

export const RenderOAuth2 = ({ children }: RenderOAuth2Props) => {
  const { oauth2Data, startAuthenticationRequestResponse, state, isTimeout, recaptchaError } = useOAuth2();
  const location = useLocation();
  const { t } = useTranslation();
  const currentPage = window.location.pathname;

  if (hasRequiredOAuth2Parameters(oauth2Data)) {
    persistRedirectUri(oauth2Data.redirect_uri);
  }

  const earlyResult = resolveEarlyExitResult(
    isTimeout, startAuthenticationRequestResponse, recaptchaError, t,
    currentPage, location, state, oauth2Data, children
  );
  if (earlyResult) return earlyResult;

  const authFlowResult = handleAuthenticationFlow(oauth2Data, startAuthenticationRequestResponse, isTimeout || false, recaptchaError, t, currentPage);
  if (authFlowResult) {
    return authFlowResult;
  }
  
  return <>{children}</>;
};

function resolveEarlyExitResult(
  isTimeout: boolean | undefined,
  startAuthenticationRequestResponse: any,
  recaptchaError: string | null,
  t: (key: string) => string,
  currentPage: string,
  location: any,
  state: string,
  oauth2Data: OAuth2Type,
  children: ReactNode,
): React.ReactElement | null {
  const steps = [
    () => isTimeout ? handleAuthenticationResponse(startAuthenticationRequestResponse, isTimeout, recaptchaError, t) : null,
    () => handleEmailNotVerifiedRedirect(location, state),
    () => handleProviderEmailNotVerifiedError(location, state),
    () => handleOAuthAuthorizedCallbackError(currentPage, location, state),
    () => handleCallbackUrlRedirect(currentPage, oauth2Data.redirect_uri, startAuthenticationRequestResponse, location),
    () => handleRouteValidation(currentPage, oauth2Data, children),
    () => handleStateValidation(state, currentPage, oauth2Data, location),
  ];

  for (const step of steps) {
    const result = step();
    if (result) return result;
  }

  return null;
}

function hasRequiredOAuth2Parameters(
  oauth2QueryParameters: OAuth2Type
): boolean {
  const { client_id, response_type, redirect_uri } = oauth2QueryParameters;
  return Boolean(client_id && response_type && redirect_uri);
}

function getMissingOauth2Arguments(
  oauth2QueryParameters: OAuth2Type
): string[] {
  const requiredFields: Array<[string, string]> = [
    [oauth2QueryParameters.client_id, OAuth2QueryParameters.CLIENT_ID],
    [oauth2QueryParameters.redirect_uri, OAuth2QueryParameters.REDIRECT_URI],
    [oauth2QueryParameters.scope, OAuth2QueryParameters.SCOPE],
    [oauth2QueryParameters.response_type, OAuth2QueryParameters.RESPONSE_TYPE],
  ];
  return requiredFields.filter(([value]) => !value).map(([, name]) => name);
}

function isValidApplicationRoute(path: string): boolean {
  // Define valid application routes (excluding root route which needs special handling)
  const validRoutes = [
    "/login/identity",
    "/login/password", 
    "/login/verifyemail",
    "/login/verifyemailaddress",
    "/login/sendrecoverypasswordlink",
    "/login/recoverypassword",
    "/login/secondfactor",
    "/signup/createaccount",
    "/signup/personalinformation",
    "/signup/identity",
    "/signup/password",
    "/signup/verifyemail",
    "/signup/congratulations",
    "/onboarding",
    "/serviceterms"
  ];

  // Check exact matches
  if (validRoutes.includes(path)) {
    return true;
  }

  // Check for service terms sub-routes
  return path.startsWith("/serviceterms/");
}
